import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Loader2 } from 'lucide-react';
import { useUser, useIsAuthenticated, useHasHydrated } from '@/stores';

interface GuestRouteProps {
  children: ReactNode;
}

export function GuestRoute({ children }: GuestRouteProps) {
  const navigate = useNavigate();
  const user = useUser();
  const isAuthenticated = useIsAuthenticated();
  const isHydrated = useHasHydrated();
  const hasRedirected = useRef(false);
  const [isReady, setIsReady] = useState(false);

  // Wait for hydration + a small delay to ensure store consistency
  useEffect(() => {
    if (!isHydrated) {
      setIsReady(false);
      return;
    }

    // Small delay after hydration to ensure store is fully consistent
    const timeoutId = setTimeout(() => {
      setIsReady(true);
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [isHydrated]);

  useEffect(() => {
    if (!isReady || hasRedirected.current) return;

    // Only redirect if BOTH isAuthenticated AND user are valid
    // This prevents redirecting when store is in an inconsistent state
    if (isAuthenticated && user) {
      hasRedirected.current = true;
      const role = user.role?.toLowerCase().replace(/_/g, '') || '';
      navigate({ to: role === 'superadmin' ? '/admin/tenants' : '/dashboard' });
    }
  }, [isReady, isAuthenticated, user, navigate]);

  if (!isReady) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  // If authenticated with user, show nothing (redirect is happening)
  if (isAuthenticated && user) {
    return null;
  }

  return <>{children}</>;
}
